import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { 
  Package, 
  MapPin, 
  Calendar, 
  User, 
  Truck, 
  Eye,
  Check,
  X,
  FileText,
  Image,
  Building,
  ChevronDown,
  ChevronUp,
  Edit,
  RotateCcw,
  Trash2,
  Search,
  Filter,
  CalendarPlus,
  GripVertical,
  ArrowRight,
  ExternalLink,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { SettlementApprovalChip } from '@/components/admin/SettlementApprovalChip';

// Draggable Card Component
const DraggableCard = ({ order, columnId, children, isDragDisabled }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.id,
    data: { order, columnId },
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragDisabled ? 'default' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="relative">
        {!isDragDisabled && (
          <div 
            {...listeners}
            className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 bg-gradient-to-r from-gray-100 to-transparent rounded-l-lg opacity-0 hover:opacity-100 transition-opacity"
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

// Droppable Column Component
const DroppableColumn = ({ column, children, isOver }) => {
  const { setNodeRef } = useDroppable({
    id: column.id,
  });

  return (
    <div 
      ref={setNodeRef}
      className={`flex-shrink-0 w-80 rounded-xl border transition-all duration-200 ${
        isOver 
          ? `${column.bgColor} ring-2 ring-offset-2 ${column.ringColor || 'ring-blue-400'} scale-[1.02]` 
          : `${column.bgColor} ${column.borderColor}`
      }`}
      data-testid={`kanban-column-${column.id}`}
    >
      {children}
    </div>
  );
};

const OrdersKanban = ({ 
  orders = [], 
  drivers = [],
  clients = [],
  downloadingContractOrderId = null,
  filters,
  setFilters,
  onEditOrder,
  onCompleteService,
  onViewHistory,
  onRevertToPending,
  onDeleteOrder,
  onCancelOrder,
  onViewPhotos,
  onViewExpenses,
  onDownloadContract,
  onAddToCalendar,
  onApproveTransitCancellation,
  onCancelAtPickup,
  onCancelInTransit,
  onAssignDriver,
  onApproveService,
  canSeeClientPrices = true,
  canDelete = true
}) => {
  const { t, i18n } = useTranslation();
  const billingDateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const [expandedCards, setExpandedCards] = useState({});
  const [collapsedColumns, setCollapsedColumns] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [driverFilterOpen, setDriverFilterOpen] = useState(false);
  const [overId, setOverId] = useState(null);
  
  // Drag confirmation dialog states
  const [showDragConfirmDialog, setShowDragConfirmDialog] = useState(false);
  const [pendingDragAction, setPendingDragAction] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Filter orders based on current filters
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Exclude approved orders (historical)
      if (order.approval_status === 'approved') return false;

      // Search by client name or email
      if (filters.searchClient && 
          !order.client_name?.toLowerCase().includes(filters.searchClient.toLowerCase()) &&
          !order.client_email?.toLowerCase().includes(filters.searchClient.toLowerCase())) {
        return false;
      }

      // Search by plate
      if (filters.searchPlate && !order.car_plate?.toLowerCase().includes(filters.searchPlate.toLowerCase())) {
        return false;
      }

      // Filter by order status
      if (filters.orderStatus !== 'all' && order.status !== filters.orderStatus) {
        return false;
      }

      // Filter by payment status
      if (filters.paymentStatus !== 'all' && order.payment_status !== filters.paymentStatus) {
        return false;
      }

      // Filter by driver
      if (filters.driverFilter !== 'all') {
        if (filters.driverFilter === 'no-driver' && order.driver_id) {
          return false;
        }
        if (filters.driverFilter !== 'no-driver' && order.driver_id !== filters.driverFilter) {
          return false;
        }
      }

      // Filter by city
      if (filters.city !== 'all' && 
          !order.pickup_address?.toLowerCase().includes(filters.city.toLowerCase()) &&
          !order.delivery_address?.toLowerCase().includes(filters.city.toLowerCase())) {
        return false;
      }

      // Filter by date range
      if (filters.dateFrom || filters.dateTo) {
        const orderDate = new Date(order.created_at);
        if (filters.dateFrom && orderDate < new Date(filters.dateFrom)) return false;
        if (filters.dateTo) {
          const endDate = new Date(filters.dateTo);
          endDate.setHours(23, 59, 59, 999);
          if (orderDate > endDate) return false;
        }
      }

      return true;
    });
  }, [orders, filters]);

  // Categorize orders into columns
  const columns = useMemo(() => [
    {
      id: 'pending',
      title: t('admin_kanban_new'),
      subtitle: t('admin_kanban_new_subtitle'),
      color: 'red',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      headerBg: 'bg-gradient-to-r from-red-500 to-red-600',
      cardBorder: 'border-l-red-500',
      ringColor: 'ring-red-400',
      orders: filteredOrders.filter(o =>
        ['pending', 'confirmed', 'por_asignar'].includes(o.status) && !o.driver_id
      )
    },
    {
      id: 'assigned',
      title: t('admin_kanban_assigned'),
      subtitle: t('admin_kanban_assigned_subtitle'),
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      headerBg: 'bg-gradient-to-r from-blue-500 to-blue-600',
      cardBorder: 'border-l-blue-500',
      ringColor: 'ring-blue-400',
      orders: filteredOrders.filter(o =>
        (
          o.status === 'assigned' ||
          o.status === 'in_progress' ||
          o.status === 'in_transit' ||
          o.status === 'en_route' ||
          o.status === 'picked_up' ||
          (o.status === 'pending' && o.driver_id) ||
          (o.status === 'confirmed' && o.driver_id) ||
          (o.status === 'por_asignar' && o.driver_id)
        ) &&
        o.approval_status !== 'pending_approval'
      )
    },
    {
      id: 'pending_approval',
      title: t('admin_kanban_pending_approval'),
      subtitle: t('admin_kanban_pending_approval_subtitle'),
      color: 'orange',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      headerBg: 'bg-gradient-to-r from-orange-500 to-orange-600',
      cardBorder: 'border-l-orange-500',
      ringColor: 'ring-orange-400',
      orders: filteredOrders.filter(o => o.approval_status === 'pending_approval')
    },
    {
      id: 'completed',
      title: t('admin_kanban_completed'),
      subtitle: t('admin_kanban_completed_subtitle'),
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      headerBg: 'bg-gradient-to-r from-green-500 to-green-600',
      cardBorder: 'border-l-green-500',
      ringColor: 'ring-green-400',
      orders: filteredOrders.filter(o => 
        (o.status === 'delivered' && o.approval_status !== 'approved') ||
        (o.is_fully_completed && o.approval_status !== 'approved')
      )
    }
  ], [filteredOrders, t]);

  // Get column index for ordering
  const getColumnIndex = (columnId) => {
    const order = ['pending', 'assigned', 'pending_approval', 'completed'];
    return order.indexOf(columnId);
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveOrder(active.data.current?.order);
  };

  // Handle drag over
  const handleDragOver = (event) => {
    const { over } = event;
    setOverId(over?.id || null);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    setActiveOrder(null);
    setOverId(null);

    if (!over) return;

    const sourceColumnId = active.data.current?.columnId;
    const targetColumnId = over.id;
    const order = active.data.current?.order;

    // If dropped in the same column, do nothing
    if (sourceColumnId === targetColumnId) return;

    // Process the drag based on source and target
    processDragAction(order, sourceColumnId, targetColumnId);
  };

  // Process drag action based on columns
  const processDragAction = (order, sourceColumn, targetColumn) => {
    const sourceIndex = getColumnIndex(sourceColumn);
    const targetIndex = getColumnIndex(targetColumn);
    const isForward = targetIndex > sourceIndex;

    // Define transitions and their handlers
    if (sourceColumn === 'pending' && targetColumn === 'assigned') {
      // Nuevos → Asignados: Need to assign a driver
      if (onAssignDriver) {
        onAssignDriver(order);
      } else if (onEditOrder) {
        onEditOrder(order);
      }
    } 
    else if (sourceColumn === 'assigned' && targetColumn === 'pending_approval') {
      // Asignados → Por Aprobar: Open complete service dialog
      if (onCompleteService) {
        onCompleteService(order);
      }
    }
    else if (sourceColumn === 'pending_approval' && targetColumn === 'completed') {
      // Por Aprobar → Completados: Approve the service
      setPendingDragAction({
        type: 'approve',
        order,
        sourceColumn,
        targetColumn,
        title: t('admin_approve_service'),
        message: t('admin_approve_service_message', { id: order.id?.substring(0, 8).toUpperCase() })
      });
      setShowDragConfirmDialog(true);
    }
    else if (!isForward) {
      // Moving backwards - show confirmation dialog
      const targetName = columns.find(c => c.id === targetColumn)?.title || targetColumn;
      setPendingDragAction({
        type: 'revert',
        order,
        sourceColumn,
        targetColumn,
        title: t('admin_revert_state'),
        message: t('admin_revert_state_message', { id: order.id?.substring(0, 8).toUpperCase(), target: targetName })
      });
      setShowDragConfirmDialog(true);
    }
  };

  // Execute pending drag action
  const executePendingAction = () => {
    if (!pendingDragAction) return;

    const { type, order } = pendingDragAction;

    if (type === 'approve' && onApproveService) {
      onApproveService(order);
    } else if (type === 'revert') {
      // For revert, open the edit dialog or complete service dialog
      if (pendingDragAction.targetColumn === 'pending_approval' && onCompleteService) {
        onCompleteService(order);
      } else if (onEditOrder) {
        onEditOrder(order);
      }
    }

    setShowDragConfirmDialog(false);
    setPendingDragAction(null);
  };

  const toggleCardExpand = (orderId, e) => {
    e?.stopPropagation();
    setExpandedCards(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const toggleColumnCollapse = (columnId) => {
    setCollapsedColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const getClientName = (clientId) => {
    const client = clients?.find(c => c.id === clientId);
    return client?.company_name || client?.name || t('admin_no_client');
  };

  const getDriverName = (driverId) => {
    const driver = drivers?.find(d => d.id === driverId);
    return driver?.name || t('admin_unassigned');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('admin_not_available');
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return t('admin_not_available');
      return date.toLocaleDateString(billingDateLocale, { day: '2-digit', month: '2-digit' });
    } catch {
      return t('admin_not_available');
    }
  };

  const truncateAddress = (address, maxLength = 30) => {
    if (!address) return t('admin_not_available');
    return address.length > maxLength ? address.substring(0, maxLength) + '...' : address;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('admin_status_pending') },
      assigned: { bg: 'bg-blue-100', text: 'text-blue-800', label: t('admin_status_assigned') },
      in_progress: { bg: 'bg-purple-100', text: 'text-purple-800', label: t('admin_status_in_progress') },
      in_transit: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: t('admin_status_in_transit') },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', label: t('admin_status_delivered') },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: t('admin_status_cancelled') }
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus) => {
    const config = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-800', label: t('admin_payment_pending') },
      paid: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: t('admin_payment_paid') },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: t('admin_payment_failed') }
    };
    const c = config[paymentStatus] || config.pending;
    return (
      <span
        className={`inline-flex text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap shrink-0 ${c.bg} ${c.text}`}
      >
        {c.label}
      </span>
    );
  };

  // Order Card Component (used both in column and overlay)
  const OrderCard = ({ order, columnId, isOverlay = false }) => {
    const isExpanded = expandedCards[order.id] && !isOverlay;
    const isCancelled = order.status?.includes('Cancelado') || order.status === 'cancelled';
    const needsTransitApproval = order.status === 'Cancelado en Transporte - Pendiente';
    const column = columns.find(c => c.id === columnId);
    
    return (
      <div 
        className={`bg-white rounded-lg shadow-sm border-l-4 ${
          column?.cardBorder || 'border-l-gray-400'
        } hover:shadow-md transition-all duration-200 ${isOverlay ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
        data-testid={`kanban-card-${order.id}`}
        data-order-id={order.id}
      >
        {/* Card Header - Always visible */}
        <div className="p-3 cursor-pointer" onClick={(e) => !isOverlay && toggleCardExpand(order.id, e)}>
          {/* Top row: ID, Service Type, Status */}
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-700 font-mono">
                #{order.id?.substring(0, 8).toUpperCase()}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-[#36e961] text-white font-medium">
                {t(order.service_type)}
              </span>
              {order.has_incident && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-medium flex items-center gap-0.5" title={t('admin_dash_incident_tooltip')}>
                  <AlertTriangle className="w-3 h-3" />
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-1">
              {getStatusBadge(order.status)}
              <SettlementApprovalChip order={order} t={t} />
            </div>
          </div>

          {/* Route with icons */}
          <div className="flex items-start gap-1 text-sm mb-2">
            <div className="flex flex-col items-center mr-1">
              <MapPin className="w-3 h-3 text-green-600" />
              <div className="w-px h-3 bg-gray-300"></div>
              <MapPin className="w-3 h-3 text-red-600" />
              {order.service_type === 'bc_service' && order.point_c_address && (
                <>
                  <div className="w-px h-3 bg-gray-300"></div>
                  <MapPin className="w-3 h-3 text-orange-500" />
                </>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-600 truncate" title={order.pickup_address}>
                {truncateAddress(order.pickup_address, 35)}
              </p>
              <p className="text-xs text-gray-600 truncate" title={order.delivery_address}>
                {truncateAddress(order.delivery_address, 35)}
              </p>
              {order.service_type === 'bc_service' && order.point_c_address && (
                <p className="text-xs text-orange-600 truncate" title={`${t('admin_kanban_point_c_label')} ${order.point_c_address}`}>
                  → {truncateAddress(order.point_c_address, 35)}
                </p>
              )}
            </div>
          </div>

          {/* Vehicle info */}
          <div className="flex items-center gap-2 text-xs text-gray-700 mb-2">
            <Truck className="w-3 h-3 text-gray-500" />
            <span className="font-medium">{order.car_brand} {order.car_model}</span>
            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 font-semibold">
              {order.car_plate}
            </span>
          </div>

          {/* Date, driver, price & payment — two rows so long payment labels never overlap */}
          <div className="text-xs border-t pt-2 mt-2 space-y-2">
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="flex items-center gap-1 text-gray-500 shrink-0">
                  <Calendar className="w-3 h-3 shrink-0" />
                  <span>{formatDate(order.pickup_date_from || order.service_date)}</span>
                </div>
                {order.driver_id && (
                  <div className="flex items-center gap-1 text-blue-600 min-w-0">
                    <User className="w-3 h-3 shrink-0" />
                    <span className="truncate" title={getDriverName(order.driver_id)}>
                      {getDriverName(order.driver_id)}
                    </span>
                  </div>
                )}
              </div>
              <div className="shrink-0 text-right">
                {canSeeClientPrices ? (
                  <span className="font-bold text-green-600 whitespace-nowrap">
                    €{order.total_price?.toFixed(2) || '0.00'}
                  </span>
                ) : (
                  <span className="font-bold text-gray-400">---</span>
                )}
              </div>
            </div>
            <div className="flex justify-end w-full min-w-0">
              {getPaymentBadge(order.payment_status)}
            </div>
          </div>

          {/* Expand indicator */}
          {!isOverlay && (
            <div className="flex justify-center mt-2 opacity-50 group-hover:opacity-100 transition-opacity">
              {isExpanded ? 
                <ChevronUp className="w-4 h-4 text-gray-400" /> : 
                <ChevronDown className="w-4 h-4 text-gray-400" />
              }
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {isExpanded && !isOverlay && (
          <div className="border-t px-3 py-3 bg-gray-50/80" onClick={(e) => e.stopPropagation()}>
            {/* Client & Driver Details */}
            <div className="grid grid-cols-2 gap-3 text-xs mb-3">
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <Building className="w-3 h-3" />
                  <span className="font-medium">{t('admin_client_label')}</span>
                </div>
                <p className="text-gray-700 truncate pl-4">{getClientName(order.client_id)}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <User className="w-3 h-3" />
                  <span className="font-medium">{t('admin_driver_label')}</span>
                </div>
                <p className="text-gray-700 truncate pl-4">
                  {order.driver_id ? (
                    <>
                      {order.driver_name || getDriverName(order.driver_id)}
                      {order.driver_phone && (
                        <span className="text-gray-400 ml-1">({order.driver_phone})</span>
                      )}
                    </>
                  ) : (
                    <span className="text-orange-500">{t('admin_unassigned')}</span>
                  )}
                </p>
              </div>
            </div>

            {/* Pricing info */}
            <div className="grid grid-cols-2 gap-2 text-xs mb-3 p-2 bg-white rounded border">
              {canSeeClientPrices && (
                <div>
                  <span className="text-gray-500">{t('admin_price_client')} </span>
                  <span className="font-semibold text-green-600">€{order.total_price?.toFixed(2) || '0.00'}</span>
                </div>
              )}
              <div className={canSeeClientPrices ? '' : 'col-span-2'}>
                <span className="text-gray-500">{t('admin_price_driver')} </span>
                <span className="font-semibold text-blue-600">€{order.driver_price?.toFixed(2) || '0.00'}</span>
              </div>
            </div>

            {/* Distance and Service Info */}
            {order.distance_km && (
              <div className="text-xs text-gray-500 mb-3">
                <span>{t('admin_distance')} </span>
                <span className="font-medium text-gray-700">{order.distance_km} km</span>
              </div>
            )}

            {/* Second Vehicle for B-C / Trade-In services */}
            {(order.service_type === 'bc_service' || order.service_type === 'tradein') && order.second_vehicle && (
              <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
                <p className="font-semibold text-orange-700 mb-1 flex items-center gap-1">
                  <Truck className="w-3 h-3" /> {t('second_vehicle')}
                </p>
                <p className="text-gray-700">{order.second_vehicle.car_brand} {order.second_vehicle.car_model} - <span className="font-mono font-semibold">{order.second_vehicle.car_plate}</span></p>
                {order.service_type === 'bc_service' && order.point_c_address && (
                  <p className="text-gray-600 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-orange-500" /> {t('admin_kanban_point_c_label')} {order.point_c_address}
                  </p>
                )}
                {order.service_type === 'bc_service' && order.point_c_contact_name && (
                  <p className="text-gray-500">{order.point_c_contact_name} • {order.point_c_contact_phone}</p>
                )}
              </div>
            )}

            {/* Completion Status for pending_approval */}
            {columnId === 'pending_approval' && (
              <div className="flex flex-wrap gap-1 mb-3">
                <span className={`text-xs px-2 py-1 rounded ${order.has_pickup_photos ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  📷 {t('admin_photos_pickup')}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${order.has_delivery_photos ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  📷 {t('admin_photos_delivery')}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${order.has_expense_report ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  💰 {t('admin_expenses')}
                </span>
              </div>
            )}

            {/* Action Buttons - Always visible */}
            <div className="space-y-2">
              {/* Primary Actions Row */}
              <div className="flex flex-wrap gap-1">
                {/* Abrir en nueva pestaña - Always */}
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2 bg-[#36e961]/10 border-[#36e961] text-[#2aa84a] hover:bg-[#36e961]/20"
                  onClick={() => window.open(`/admin/servicio/${order.id}`, '_blank')}
                  data-testid={`kanban-open-${order.id}`}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  {t('admin_open')}
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2"
                  onClick={() => onEditOrder && onEditOrder(order)}
                  data-testid={`kanban-edit-${order.id}`}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  {t('admin_edit')}
                </Button>

                {!isCancelled && order.status !== 'delivered' && (
                  <Button
                    size="sm"
                    className="text-xs h-7 px-2 bg-green-600 hover:bg-green-700"
                    onClick={() => onCompleteService && onCompleteService(order)}
                    data-testid={`kanban-complete-${order.id}`}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    {t('admin_complete')}
                  </Button>
                )}

                {order.status === 'delivered' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 px-2 border-blue-400 text-blue-700"
                    onClick={() => onViewHistory && onViewHistory(order)}
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    {t('admin_history')}
                  </Button>
                )}

                {(order.status === 'delivered' || order.status === 'completed') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 px-2 border-orange-400 text-orange-700"
                    onClick={() => onRevertToPending && onRevertToPending(order)}
                    title={t('admin_revert_title')}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    {t('admin_revert')}
                  </Button>
                )}
              </div>

              {/* Secondary Actions Row */}
              <div className="flex flex-wrap gap-1">
                {/* Ver Fotos - For pending_approval and completed */}
                {(columnId === 'pending_approval' || columnId === 'completed' || order.status === 'delivered') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 px-2"
                    onClick={() => onViewPhotos && onViewPhotos(order.id)}
                  >
                    <Image className="w-3 h-3 mr-1" />
                    {t('admin_photos')}
                  </Button>
                )}

                {order.driver_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 px-2"
                    disabled={downloadingContractOrderId != null}
                    onClick={() => onDownloadContract && onDownloadContract(order)}
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    {t('admin_contract')}
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2"
                  onClick={() => onAddToCalendar && onAddToCalendar(order)}
                >
                  <CalendarPlus className="w-3 h-3 mr-1" />
                  {t('admin_calendar_btn')}
                </Button>

                {canDelete && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => onDeleteOrder && onDeleteOrder(order.id)}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    {t('admin_delete')}
                  </Button>
                )}
              </div>

              {/* Cancel Actions Row - Only for non-cancelled, non-delivered */}
              {!isCancelled && order.status !== 'delivered' && (
                <div className="flex flex-wrap gap-1 pt-1 border-t">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="text-xs h-7 px-2"
                    onClick={() => onCancelOrder && onCancelOrder(order)}
                  >
                    <X className="w-3 h-3 mr-1" />
                    {t('admin_cancel')}
                  </Button>
                  
                  {(order.status === 'assigned' || order.status === 'in_transit') && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2 border-red-300 text-red-700"
                        onClick={() => onCancelAtPickup && onCancelAtPickup(order)}
                        title={t('admin_cancel_at_pickup_title')}
                      >
                        🚫 {t('admin_cancel_at_pickup')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2 border-red-300 text-red-700"
                        onClick={() => onCancelInTransit && onCancelInTransit(order)}
                        title={t('admin_cancel_in_transit_title')}
                      >
                        🛑 {t('admin_cancel_in_transit')}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Approve transit cancellation */}
              {needsTransitApproval && (
                <div className="pt-1 border-t">
                  <Button
                    size="sm"
                    className="text-xs h-7 px-2 bg-green-600 hover:bg-green-700 w-full"
                    onClick={() => onApproveTransitCancellation && onApproveTransitCancellation(order)}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    {t('admin_approve_cancellation')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters Panel */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold">{t('admin_filters')}</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">{t('admin_filter_client')}</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                <Input
                  placeholder={t('admin_search_client_placeholder')}
                  value={filters.searchClient}
                  onChange={(e) => setFilters({ ...filters, searchClient: e.target.value })}
                  className="pl-7 h-8 text-sm"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">{t('admin_plate')}</Label>
              <Input
                placeholder={t('admin_plate_placeholder')}
                value={filters.searchPlate}
                onChange={(e) => setFilters({ ...filters, searchPlate: e.target.value })}
                className="h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">{t('admin_city')}</Label>
              <Input
                placeholder={t('admin_city_placeholder')}
                value={filters.city === 'all' ? '' : filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value || 'all' })}
                className="h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">{t('admin_driver_filter')}</Label>
              <Popover open={driverFilterOpen} onOpenChange={setDriverFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={driverFilterOpen}
                    className="h-8 w-full justify-between font-normal text-sm px-3"
                  >
                    {filters.driverFilter === 'all'
                      ? t('admin_all')
                      : filters.driverFilter === 'no-driver'
                        ? t('admin_unassigned')
                        : drivers.find((d) => d.id === filters.driverFilter)?.name ?? filters.driverFilter}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command
                    filter={(value, search) => {
                      if (value === 'all' || value === 'no-driver') return 1;
                      const driver = drivers.find((d) => d.id === value);
                      if (!driver) return 0;
                      if (!search) return 1;
                      return driver.name?.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder={t('search_driver')} />
                    <CommandList>
                      <CommandEmpty>{t('type_to_search')}</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setFilters({ ...filters, driverFilter: 'all' });
                            setDriverFilterOpen(false);
                          }}
                        >
                          {t('admin_all')}
                        </CommandItem>
                        <CommandItem
                          value="no-driver"
                          onSelect={() => {
                            setFilters({ ...filters, driverFilter: 'no-driver' });
                            setDriverFilterOpen(false);
                          }}
                        >
                          {t('admin_unassigned')}
                        </CommandItem>
                        {drivers.map((driver) => (
                          <CommandItem
                            key={driver.id}
                            value={driver.id}
                            onSelect={() => {
                              setFilters({ ...filters, driverFilter: driver.id });
                              setDriverFilterOpen(false);
                            }}
                          >
                            {driver.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-xs">{t('admin_from')}</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">{t('admin_to')}</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="h-8 text-sm"
              />
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs w-full"
                onClick={() => setFilters({
                  searchClient: '',
                  searchPlate: '',
                  orderStatus: 'all',
                  paymentStatus: 'all',
                  driverFilter: 'all',
                  city: 'all',
                  dateFrom: '',
                  dateTo: ''
                })}
              >
                {t('admin_clear_filters')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drag hint */}
      <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
        <GripVertical className="w-4 h-4 text-blue-500" />
        <span><strong>{t('admin_tip_label')}</strong> {t('admin_kanban_tip')}</span>
        <ArrowRight className="w-4 h-4 text-blue-400" />
      </div>

      <div className="text-sm text-gray-600 flex items-center gap-2">
        <span>{filteredOrders.length} {t('admin_services_in_process')}</span>
        <span className="text-gray-300">|</span>
        {columns.map((col, idx) => (
          <span key={col.id} className="text-xs">
            {col.title}: <strong>{col.orders.length}</strong>
            {idx < columns.length - 1 && <span className="mx-1">•</span>}
          </span>
        ))}
      </div>

      {/* Kanban Board with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '600px' }} data-testid="kanban-board">
          {columns.map(column => (
            <DroppableColumn 
              key={column.id} 
              column={column}
              isOver={overId === column.id}
            >
              {/* Column Header */}
              <div 
                className={`${column.headerBg} text-white px-4 py-3 rounded-t-xl cursor-pointer`}
                onClick={() => toggleColumnCollapse(column.id)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-sm">{column.title}</h3>
                    <p className="text-xs text-white/80">{column.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-white/20 px-2.5 py-1 rounded-full text-sm font-bold">
                      {column.orders.length}
                    </span>
                    {collapsedColumns[column.id] ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronUp className="w-4 h-4" />
                    }
                  </div>
                </div>
              </div>

              {/* Column Content */}
              {!collapsedColumns[column.id] && (
                <div className="p-3 space-y-3 max-h-[calc(100vh-350px)] overflow-y-auto">
                  {column.orders.length === 0 ? (
                    <div className={`text-center py-8 text-gray-400 border-2 border-dashed rounded-lg ${
                      overId === column.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                    }`}>
                      <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">
                        {overId === column.id ? t('admin_drop_here') : t('admin_no_services')}
                      </p>
                    </div>
                  ) : (
                    column.orders.map(order => (
                      <DraggableCard 
                        key={order.id} 
                        order={order} 
                        columnId={column.id}
                        isDragDisabled={expandedCards[order.id]}
                      >
                        <OrderCard order={order} columnId={column.id} />
                      </DraggableCard>
                    ))
                  )}
                </div>
              )}
            </DroppableColumn>
          ))}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId && activeOrder ? (
            <div className="w-80">
              <OrderCard 
                order={activeOrder} 
                columnId={columns.find(c => c.orders.some(o => o.id === activeId))?.id || 'pending'}
                isOverlay={true}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Drag Confirmation Dialog */}
      <Dialog open={showDragConfirmDialog} onOpenChange={setShowDragConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              {pendingDragAction?.title || t('admin_confirm_action')}
            </DialogTitle>
            <DialogDescription>
              {pendingDragAction?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowDragConfirmDialog(false);
                setPendingDragAction(null);
              }}
            >
              {t('admin_cancel')}
            </Button>
            <Button
              onClick={executePendingAction}
              className="bg-[#36e961] hover:bg-[#2dd351]"
            >
              {t('admin_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersKanban;
